import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Collapse, Paper, Typography } from "@mui/material";

import type { TimeRangeOnWeekday } from "@thalamos/common";
import type { CalendarProviderType } from "@thalamos/aspire-common";

import type { CalendarSyncSummary } from "./CalendarSyncSection.js";
import { PROVIDER_LABELS } from "./CalendarSyncSection.js";
import { WEEK_STARTS_ON } from "./UpcomingAvailabilityCalendar.js";
import {
  AvailabilityGrid,
  AvailabilityGridLegend,
  AvailabilityGridSummary,
  blocksToWeekdayRanges,
  buildWeekdayColumns,
  Button,
  weekdayRangesToBlocks,
} from "~/components/design-system/index.js";
import { ExpandMore } from "~/components/layout/styleWrappers.js";

const COLUMNS = buildWeekdayColumns(WEEK_STARTS_ON);

export interface DefaultWorkingHoursSectionProps {
  defaultWeek: readonly TimeRangeOnWeekday[];
  onDefaultWeekChange: (defaultWeek: TimeRangeOnWeekday[]) => void;
  calendarSyncActive: boolean;
  calendarSummary: CalendarSyncSummary;
  onManageCalendar: () => void;
}

function providerLabel(providerType: CalendarProviderType): string {
  return PROVIDER_LABELS[providerType];
}

/**
 * The doctor's repeating weekly hours and their calendar connection, the two
 * inputs everything downstream of them is derived from.
 *
 * Opens collapsed once hours exist: a returning doctor rarely changes them,
 * so the setup furniture gives up its space. Confirm persists nothing - the
 * owner saves through `onDefaultWeekChange` as hours are painted - it only
 * collapses the section as a nudge on to the next step.
 */
export function DefaultWorkingHoursSection({
  defaultWeek,
  onDefaultWeekChange,
  calendarSyncActive,
  calendarSummary,
  onManageCalendar,
}: DefaultWorkingHoursSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultWeek.length === 0);
  // Confirm waits until some hours are offered in this visit, so an empty week
  // is not folded away by mistake; clearing every slot after that is opting
  // out, and may be confirmed.
  const [hoursOfferedThisVisit, setHoursOfferedThisVisit] = useState(
    defaultWeek.length > 0,
  );
  if (defaultWeek.length > 0 && !hoursOfferedThisVisit) {
    setHoursOfferedThisVisit(true);
  }
  const blocks = weekdayRangesToBlocks(defaultWeek, WEEK_STARTS_ON);

  const connectedProviders = calendarSummary.connections
    .map((connection) => providerLabel(connection.providerType))
    .join(", ");
  const collapsedSummary = [
    defaultWeek.length > 0 ? "Weekly hours set" : "No weekly hours set",
    connectedProviders
      ? `${connectedProviders} connected`
      : "no calendar connected",
  ].join(", ");

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="section12-working-hours-section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Default Working Hours</Typography>
          {!isExpanded && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
              data-testid="default-working-hours-summary"
            >
              {collapsedSummary}
            </Typography>
          )}
        </Box>
        <ExpandMore
          isExpanded={isExpanded}
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? "Collapse default working hours"
              : "Expand default working hours"
          }
          data-testid="default-working-hours-expand"
        >
          <ExpandMoreIcon sx={{ color: "primary.main" }} />
        </ExpandMore>
      </Box>
      <Collapse in={isExpanded}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 1,
            mb: 2,
          }}
        >
          {calendarSyncActive
            ? "These act as a ceiling — you're only shown as available when both your working hours and calendar agree."
            : "Set the times you are generally available each week. This is the foundation of your availability."}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Button
            variant="outlined"
            label="Sync Calendar"
            customStartIcon={<SyncIcon />}
            onClick={onManageCalendar}
            sx={{ "& .MuiButton-startIcon": { color: "inherit" } }}
            testId="sync-calendar-btn"
          />
          <Button
            variant="contained"
            label="Confirm"
            disabled={!hoursOfferedThisVisit}
            onClick={() => {
              setIsExpanded(false);
            }}
            testId="default-working-hours-confirm"
          />
        </Box>
        <AvailabilityGrid
          columns={COLUMNS}
          value={blocks}
          onChange={(blocks) => {
            onDefaultWeekChange(blocksToWeekdayRanges(blocks, WEEK_STARTS_ON));
          }}
          data-testid="default-working-hours-grid"
        />
        <Box sx={{ mt: 1.5 }}>
          <AvailabilityGridLegend
            swatches={["available", "unavailable"]}
            data-testid="default-working-hours-legend"
          />
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <AvailabilityGridSummary
            columns={COLUMNS}
            value={blocks}
            data-testid="default-working-hours-summary-lines"
          />
        </Box>
      </Collapse>
    </Paper>
  );
}
